<div class="w-full xl:w-80 flex flex-col gap-4">
  <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4">
    <h3 class="text-xs font-semibold text-slate-500 tracking-widest uppercase mb-3">
      Total Population Coverage
    </h3>

    <div class="flex items-center gap-4">
      <div class="relative h-24 w-24">
        @if (coverageData?.datasets?.length) {
        <p-chart type="doughnut" [data]="coverageData" [options]="coverageOptions" class="block h-24 w-24"></p-chart>
        }
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="text-sm font-semibold text-slate-900">{{ centerTotalLabel }}</div>
        </div>
      </div>

      <div class="flex flex-col gap-3 text-xs font-semibold text-slate-600">
        <div class="flex items-center gap-2">
          <span class="inline-block h-2 w-2 rounded-full bg-[#aa1646]"></span>
          <span class="uppercase tracking-wide">Normal BP <br />({{ normalLabel }})</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="inline-block h-2 w-2 rounded-full bg-[#e95887]"></span>
          <span class="uppercase tracking-wide">Prehypertension<br />({{ prehtLabel }})</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="inline-block h-2 w-2 rounded-full bg-[#fec2d6]"></span>
          <span class="uppercase tracking-wide">Hypertension<br />({{ htLabel }})</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="inline-block h-2 w-2 rounded-full bg-[#d41c58]"></span>
          <span class="uppercase tracking-wide">CVD<br />({{ cvdLabel }})</span>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4">
    <h3 class="text-sm font-semibold text-slate-900 mb-3">Simulation Parameters</h3>

    @if (errorMsg) {
    <div class="mb-3 rounded-md bg-rose-50 text-rose-700 text-xs px-3 py-2 border border-rose-200">
      {{ errorMsg }}
    </div>
    }

    <div class="flex flex-col gap-4 text-xs">
      <div class="flex items-center gap-3">
        <p-knob
          [(ngModel)]="preventionKnob"
          [min]="0"
          [max]="100"
          [size]="60"
          [step]="1"
          [strokeWidth]="12"
          [valueTemplate]="'{value}%'"
          valueColor="#d41c58"
          (onChange)="onPreventionKnobChange()"
          [pTooltip]="'% of people with newly developed pre-hypertension each year'"
          tooltipPosition="left"></p-knob>
        <div class="flex-1">
          <div class="text-sm font-semibold text-slate-900">Prevention</div>
          <div class="text-[11px] uppercase text-slate-500">PREHYPERTENSION ONSET INCIDENT RATE</div>
          <div class="text-[11px] text-slate-700 mt-1">
            {{ preventionBaseDisplay }} → <span class="font-semibold">{{ preventionValueDisplay }}</span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <p-knob
          [(ngModel)]="screeningKnob"
          [min]="0"
          [max]="300"
          [size]="60"
          [step]="1"
          [strokeWidth]="12"
          [valueTemplate]="'{value}%'"
          valueColor="#d41c58"
          (onChange)="onScreeningKnobChange()"
          [pTooltip]="'% of undiagnosed people who are diagnosed each year'"
          tooltipPosition="left"></p-knob>
        <div class="flex-1">
          <div class="text-sm font-semibold text-slate-900">Screening</div>
          <div class="text-[11px] uppercase text-slate-500">DIAGNOSIS RATE</div>
          <div class="text-[11px] text-slate-700 mt-1">
            {{ screeningBaseDisplay }} → <span class="font-semibold">{{ screeningValueDisplay }}</span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <p-knob
          [(ngModel)]="treatmentKnob"
          [min]="0"
          [max]="300"
          [size]="60"
          [step]="1"
          [strokeWidth]="12"
          [valueTemplate]="'{value}%'"
          valueColor="#d41c58"
          (onChange)="onTreatmentKnobChange()"
          [pTooltip]="'% of diagnosed people who start treatment each year'"
          tooltipPosition="left"></p-knob>
        <div class="flex-1">
          <div class="text-sm font-semibold text-slate-900">Treatment</div>
          <div class="text-[11px] uppercase text-slate-500">HYPERTENSION TREATMENT UPTAKE</div>
          <div class="text-[11px] text-slate-700 mt-1">
            {{ treatmentBaseDisplay }} → <span class="font-semibold">{{ treatmentValueDisplay }}</span>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <button
          type="button"
          [disabled]="busy"
          (click)="openRunDialog()"
          class="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-60">
          <i class="pi pi-play text-sm"></i>
          <span>Run Simulation</span>
        </button>
      </div>

      <div class="mt-5 border-t border-surface-200 pt-4">
        <div class="flex items-center text-[11px] font-semibold text-slate-500 pb-2">
          <div class="w-6 flex justify-center">
            <input
              type="checkbox"
              class="h-3 w-3 rounded border-slate-300"
              [checked]="selectAll"
              (change)="toggleSelectAll()" />
          </div>
          <div class="flex-1">Simulation title</div>
          <div class="w-24 text-center">Actions</div>
        </div>

        @for (sim of sims; track sim.name) {
        <div class="flex items-center text-xs text-slate-800 py-2 border-t border-surface-100">
          <div class="w-6 flex justify-center">
            <input
              type="checkbox"
              class="h-3 w-3 rounded border-slate-300"
              [checked]="selectedNames.has(sim.name)"
              (change)="toggleRow(sim.name)" />
          </div>

          <div class="flex-1 flex items-center gap-2">
            <span class="font-medium">{{ sim.name }}</span>
          </div>

          <div class="w-24 flex justify-center gap-2 text-slate-500">
            <button type="button" class="hover:text-slate-700" title="View" (click)="openView(sim)" [disabled]="busy">
              <i class="pi pi-eye text-xs"></i>
            </button>

            @if (!isBaselineUi(sim)) {
            <button type="button" class="hover:text-slate-700" title="Edit" (click)="openRename(sim)" [disabled]="busy">
              <i class="pi pi-pencil text-xs"></i>
            </button>

            <button
              type="button"
              class="hover:text-rose-600"
              title="Delete"
              (click)="requestDeleteOne(sim)"
              [disabled]="busy">
              <i class="pi pi-trash text-xs text-rose-500"></i>
            </button>
            }
          </div>
        </div>
        }

        <div class="flex justify-between items-center mt-4">
          <button
            type="button"
            [disabled]="busy || selectedNames.size === 0"
            (click)="requestDeleteSelected()"
            class="inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
            <i class="pi pi-trash text-xs"></i>
            <span>Delete selected</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<p-dialog
  header="Run simulation"
  [(visible)]="runDialogVisible"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '360px' }">
  <div class="flex flex-col gap-2">
    <label class="text-xs font-semibold text-slate-600">Simulation name</label>
    <input pInputText [(ngModel)]="runName" />
    <div class="text-[11px] text-slate-500">Suggested Name: {{ runName }}</div>

    @if (errorMsg) {
    <div class="mt-2 rounded-md bg-rose-50 text-rose-700 text-xs px-3 py-2 border border-rose-200">
      {{ errorMsg }}
    </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="px-3 py-2 text-xs rounded-md bg-surface-200" (click)="runDialogVisible=false">
      Cancel
    </button>
    <button
      type="button"
      class="px-3 py-2 text-xs rounded-md bg-sky-600 text-white"
      (click)="confirmRun()"
      [disabled]="busy">
      Run
    </button>
  </ng-template>
</p-dialog>

<p-dialog
  header="Rename simulation"
  [(visible)]="renameDialogVisible"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '360px' }">
  <div class="flex flex-col gap-2">
    <label class="text-xs font-semibold text-slate-600">New name</label>
    <input pInputText [(ngModel)]="renameNew" />
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="px-3 py-2 text-xs rounded-md bg-surface-200" (click)="renameDialogVisible=false">
      Cancel
    </button>
    <button
      type="button"
      class="px-3 py-2 text-xs rounded-md bg-sky-600 text-white"
      (click)="confirmRename()"
      [disabled]="busy">
      Save
    </button>
  </ng-template>
</p-dialog>

<p-dialog
  header="Simulation details"
  [(visible)]="viewDialogVisible"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '420px' }">
  @if (viewSim) {
  <div class="text-xs text-slate-700 flex flex-col gap-2">
    <div><span class="font-semibold">Name:</span> {{ viewSim.name }}</div>
    <div>
      <span class="font-semibold">% change in prevention:</span>
      {{ viewSim.params['% of reduction'] | number:'1.2-2' }}
    </div>
    <div>
      <span class="font-semibold">% change of screening:</span>
      {{ viewSim.params['% of increase diagnosis'] | number:'1.2-2' }}
    </div>
    <div>
      <span class="font-semibold">% change of treatment:</span>
      {{ viewSim.params['% of increase treatment'] | number:'1.2-2' }}
    </div>
  </div>
  }
</p-dialog>

<p-dialog
  header="Delete simulation"
  [(visible)]="deleteDialogVisible"
  [modal]="true"
  [closable]="false"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '420px' }">
  <div class="text-sm text-slate-700">
    @if (deletePendingNames.length === 1) {
    Are you sure you want to delete <span class="font-semibold">{{ deletePendingNames[0] }}</span>?
    } @else {
    Are you sure you want to delete <span class="font-semibold">{{ deletePendingNames.length }}</span> simulations?
    }
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="px-3 py-2 text-xs rounded-md bg-surface-200" (click)="cancelDelete()">Cancel</button>
    <button
      type="button"
      class="px-3 py-2 text-xs rounded-md bg-rose-600 text-white"
      (click)="confirmDelete()"
      [disabled]="busy">
      Yes, delete
    </button>
  </ng-template>
</p-dialog>